import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type PermissionDeniedHint = {
  scope: "route" | "action";
  resource: string;
  actionLabel: string;
  requiredPermissions: string[];
  missingPermissions: string[];
  timestamp: string;
};

type PermissionHintContextValue = {
  latestHint: PermissionDeniedHint | null;
  isOpen: boolean;
  reportDenied: (hint: Omit<PermissionDeniedHint, "timestamp">) => void;
  closeHint: () => void;
};

const PERMISSION_HINT_EVENT = "permission-denied-hint";

const PermissionHintContext = createContext<PermissionHintContextValue | undefined>(
  undefined,
);

export const dispatchPermissionDeniedHint = (
  hint: Omit<PermissionDeniedHint, "timestamp">,
): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(PERMISSION_HINT_EVENT, {
      detail: {
        ...hint,
        timestamp: new Date().toISOString(),
      } as PermissionDeniedHint,
    }),
  );
};

export const PermissionHintProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [latestHint, setLatestHint] = useState<PermissionDeniedHint | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const reportDenied = useCallback(
    (hint: Omit<PermissionDeniedHint, "timestamp">) => {
      setLatestHint({ ...hint, timestamp: new Date().toISOString() });
      setIsOpen(true);
    },
    [],
  );

  const closeHint = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleHint = (event: Event) => {
      const customEvent = event as CustomEvent<PermissionDeniedHint>;
      if (!customEvent.detail) return;
      setLatestHint(customEvent.detail);
      setIsOpen(true);
    };

    window.addEventListener(PERMISSION_HINT_EVENT, handleHint);
    return () => window.removeEventListener(PERMISSION_HINT_EVENT, handleHint);
  }, []);

  const value = useMemo<PermissionHintContextValue>(
    () => ({
      latestHint,
      isOpen,
      reportDenied,
      closeHint,
    }),
    [latestHint, isOpen, reportDenied, closeHint],
  );

  return (
    <PermissionHintContext.Provider value={value}>
      {children}
    </PermissionHintContext.Provider>
  );
};

export const usePermissionHint = (): PermissionHintContextValue => {
  const context = useContext(PermissionHintContext);
  if (!context) {
    throw new Error("usePermissionHint must be used within PermissionHintProvider");
  }
  return context;
};
